import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import StatCard from '../common/StatCard';
import BulletChart from '../Charts/BulletChart';
import DotPlot from '../Charts/DotPlot';
import RadialBarChart from '../Charts/RadialBarChart';
import NightingaleChart from '../Charts/NightingaleChart';
import StackedBarChart from '../Charts/StackedBarChart';
import StackedColumnChart from '../Charts/StackedColumnChart';
import PlotChart from '../Charts/PlotChart';
import TableChart from '../Charts/TableChart';
import EmployeeAverageReport from '../Reports/EmployeeAverageReports';
import FeedbackOverview from './FeedbackOverview';
import './Dashboard.css';
import { 
  Users, FileText, BarChart3, Mail, TrendingUp, Target, 
  Building, Calendar, PieChart, BarChart2, Grid,
  RefreshCw, AlertCircle, BarChart, Eye, PlayCircle,
  PauseCircle, Activity, Table, Download, Filter,
  Star, Award, TrendingDown, Users2
} from 'lucide-react';

const Dashboard = () => {
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalEmployees: 0,
      totalCompanies: 0,
      activeSurveys: 0,
      totalResponses: 0,
      averageResponseRate: 0,
      completionRate: 0,
      pendingInvitations: 0
    },
    recentSurveys: [],
    chartData: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [chartErrors, setChartErrors] = useState({});

  // "Real time charts": when turned on, silently re-fetches dashboard data
  // on an interval (using the same fetchDashboardData/dashboardService
  // path the manual Refresh button already uses -- no new endpoints), so
  // the stat cards and charts stay current without the user needing to
  // click Refresh. lastUpdated drives the "Updated Xs ago" readout next to
  // the toggle so it's clear the data actually is live, not just static.
  const [liveMode, setLiveMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const LIVE_REFRESH_INTERVAL_MS = 30000;
  
  // NEW: Employee Report State
  const [showEmployeeReport, setShowEmployeeReport] = useState(false);
  const [selectedSurveyForReport, setSelectedSurveyForReport] = useState(null);
  const [employeeReportData, setEmployeeReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    fromDate: null,
    toDate: null,
    employeeIds: []
  });

  // Chart configurations with proper heights
  const chartConfigs = {
    bullet: {
      height: 500,
      width: '100%',
      showLegend: true,
      animation: true,
      colors: ['#6366f1', '#10b981', '#f59e0b']
    },
    dotplot: {
      height: 400,
      width: '100%',
      showGrid: true,
      dotSize: 8
    },
    radialbar: {
      height: 400,
      width: 400,
      innerRadius: 50,
      outerRadius: 150
    },
    nightingale: {
      height: 400,
      width: 400
    },
    stackedbar: {
      height: 400,
      width: '100%',
      showLegend: true,
      horizontal: false
    },
    stackedcolumn: {
      height: 400,
      width: '100%',
      showValues: true,
      showLegend: true
    },
    plot: {
      height: 500,
      width: '100%',
      showLegend: true,
      showGrid: true,
      pointSize: 6,
      showTrendLines: true,
      colors: {
        category: '#6366f1',
        question: '#10b981',
        excellent: '#10B981',
        good: '#3B82F6',
        average: '#F59E0B',
        'needs-improvement': '#EF4444',
        poor: '#6B7280'
      }
    },
    table: {
      height: 600,
      width: '100%',
      showSearch: true,
      striped: true,
      showPagination: true,
      pageSize: 10
    },
    // NEW: Employee Report Configuration
    employeeReport: {
      height: 700,
      width: '100%',
      showCategoryDetails: true,
      showQuestionBreakdown: true,
      colors: {
        excellent: '#10B981',
        good: '#3B82F6',
        average: '#F59E0B',
        'needs-improvement': '#EF4444',
        poor: '#6B7280'
      }
    }
  };

  const fetchDashboardData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setChartErrors({});

      const data = await dashboardService.getDashboardData(selectedTimeframe);
      setDashboardData(data);
      
      // Check for individual chart errors
      const errors = {};
      Object.keys(data.chartData || {}).forEach(chartType => {
        if (data.chartData[chartType]?.error) {
          errors[chartType] = data.chartData[chartType].error;
        }
      });
      setChartErrors(errors);
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // NEW: Fetch Employee Average Report
  const fetchEmployeeAverageReport = async (surveyId, filters = {}) => {
    try {
      setReportLoading(true);
      const report = await dashboardService.getEmployeeAverageReport(surveyId, filters);
      setEmployeeReportData(report);
      return report;
    } catch (error) {
      console.error('Error fetching employee average report:', error);
      setChartErrors(prev => ({ 
        ...prev, 
        employeeReport: error.message 
      }));
      throw error;
    } finally {
      setReportLoading(false);
    }
  };

  // NEW: Handle Employee Report Generation
  const handleGenerateEmployeeReport = async (surveyId) => {
    try {
      setSelectedSurveyForReport(surveyId);
      setShowEmployeeReport(true);
      await fetchEmployeeAverageReport(surveyId, reportFilters);
    } catch (error) {
      console.error('Failed to generate employee report:', error);
    }
  };

  // NEW: Handle Report Filter Changes
  const handleReportFilterChange = (newFilters) => {
    setReportFilters(newFilters);
    if (selectedSurveyForReport) {
      fetchEmployeeAverageReport(selectedSurveyForReport, newFilters);
    }
  };

  // NEW: Export Employee Report
  const handleExportEmployeeReport = async () => {
    if (!employeeReportData) return;
    
    try {
      await dashboardService.exportDashboardData('employee-report', {
        surveyId: selectedSurveyForReport,
        filters: reportFilters
      });
    } catch (error) {
      console.error('Error exporting employee report:', error);
      alert('Failed to export report: ' + error.message);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedTimeframe]);

  // Auto-refresh loop for "Live" mode. Runs alongside the effect above
  // rather than replacing it -- that one still owns the loading spinner
  // for a deliberate timeframe change, this one does quiet background
  // refreshes (showRefreshing=true keeps the current charts on screen
  // instead of flashing back to the loading state every 30s).
  useEffect(() => {
    if (!liveMode) return undefined;
    const intervalId = setInterval(() => {
      fetchDashboardData(true);
    }, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [liveMode, selectedTimeframe]);

  const [, forceTick] = useState(0);
  useEffect(() => {
    // Re-render once a minute so the "Updated Xm ago" label stays fresh
    // even when live mode is off and nothing else is triggering a render.
    const tickId = setInterval(() => forceTick((n) => n + 1), 60000);
    return () => clearInterval(tickId);
  }, []);

  const formatLastUpdated = (date) => {
    if (!date) return 'Not yet loaded';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return date.toLocaleTimeString();
  };

  const refreshChartData = async (chartType) => {
    try {
      setChartErrors(prev => ({ ...prev, [chartType]: null }));
      
      if (chartType === 'employeeReport' && selectedSurveyForReport) {
        const chartData = await fetchEmployeeAverageReport(selectedSurveyForReport, reportFilters);
        setDashboardData(prev => ({
          ...prev,
          chartData: {
            ...prev.chartData,
            [chartType]: chartData
          }
        }));
      } else {
        const chartData = await dashboardService.refreshChart(chartType, selectedTimeframe);
        setDashboardData(prev => ({
          ...prev,
          chartData: {
            ...prev.chartData,
            [chartType]: chartData
          }
        }));
      }
    } catch (error) {
      console.error(`Error refreshing ${chartType} data:`, error);
      setChartErrors(prev => ({ 
        ...prev, 
        [chartType]: error.message 
      }));
    }
  };

  const refreshAllData = async () => {
    await fetchDashboardData(true);
  };

  const handleCreateSurvey = () => {
    navigate('/surveys');
  };

  const handleViewSurveyResults = (surveyId) => {
    navigate(`/surveys/${surveyId}/status`);
  };

  const handleViewSurveyAnalytics = (surveyId) => {
    navigate(`/surveys/${surveyId}/analytics`);
  };

  // Check if chart has data and no errors
  const hasChartData = (chartType) => {
    const chart = dashboardData.chartData?.[chartType];
    return chart && !chart.error && chart.data && chart.data.length > 0;
  };

  // NEW: Check if employee report has data
  const hasEmployeeReportData = () => {
    return employeeReportData && employeeReportData.categories && employeeReportData.categories.length > 0;
  };

  // Prepare table data from recent surveys
  const getTableData = () => {
    if (!dashboardData.recentSurveys || dashboardData.recentSurveys.length === 0) {
      return [];
    }

    return dashboardData.recentSurveys.map(survey => ({
      'Survey Name': survey.name,
      'Status': survey.isActive ? 'Active' : 'Inactive',
      'Responses': survey.responseCount,
      'Completion Rate': survey.invitationCount > 0 
        ? `${Math.round((survey.responseCount / survey.invitationCount) * 100)}%` 
        : '0%',
    }));
  };

  // Prepare detailed analytics table data
  const getAnalyticsTableData = () => {
    if (!dashboardData.chartData?.plot?.data) {
      return [];
    }

    const plotData = dashboardData.chartData.plot.data;
    return plotData.map(item => ({
      'Type': item.type === 'category' ? 'Category' : 'Question',
      'Name': item.label.replace('Category: ', '').replace('Q: ', ''),
      'Rating': item.value.toFixed(2),
      'Performance': getPerformanceLevel(item.value),
    }));
  };

  // NEW: Prepare employee report summary data
  const getEmployeeReportSummary = () => {
    if (!employeeReportData) return null;

    return {
      overallScore: employeeReportData.overallScore,
      totalResponses: employeeReportData.totalResponses,
      responseRate: employeeReportData.responseRate,
      performanceLevel: employeeReportData.overallPerformance,
      highPerformingCategories: employeeReportData.summary?.highPerformingCategories || 0,
      needsImprovementCategories: employeeReportData.summary?.needsImprovementCategories || 0,
      totalCategories: employeeReportData.categories.length
    };
  };

  const getPerformanceLevel = (rating) => {
    if (rating >= 4.0) return 'Excellent';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3.0) return 'Average';
    if (rating >= 2.5) return 'Needs Improvement';
    return 'Poor';
  };

  // NEW: Get performance color
  const getPerformanceColor = (rating) => {
    if (rating >= 4.0) return 'text-green-600';
    if (rating >= 3.5) return 'text-blue-600';
    if (rating >= 3.0) return 'text-yellow-600';
    if (rating >= 2.5) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--accent)' }}></div>
          <p style={{ color: 'var(--muted)' }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--neg)' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--ink)' }}>Failed to load dashboard</h3>
          <p className="mb-4" style={{ color: 'var(--muted)' }}>{error}</p>
          <button
            onClick={refreshAllData}
            className="px-4 py-2 rounded-lg transition-colors"
            style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { stats, recentSurveys, chartData } = dashboardData;

  const tableData = getTableData();
  const analyticsTableData = getAnalyticsTableData();
  const employeeReportSummary = getEmployeeReportSummary();

  // Preserve chronological order. The API now returns actual response-date buckets;
  // sorting these by count would turn a time-series chart into a ranking chart.
  const responseBars = (chartData?.dotplot?.data || [])
    .map(item => ({ label: item.label || 'Date', value: Number(item.completed ?? item.value ?? 0) }))
    .filter(item => Number.isFinite(item.value));

  const ratingRows = (chartData?.stackedbar?.data || [])
    .map(item => ({ label: item.label || 'Rating', count: Number(item.value || 0), rating: Number(item.target || 0) }))
    .filter(item => item.count > 0 && item.rating > 0);
  const ratingTotal = ratingRows.reduce((sum, item) => sum + item.count, 0);
  const ratingAverage = ratingTotal > 0
    ? ratingRows.reduce((sum, item) => sum + item.rating * item.count, 0) / ratingTotal
    : 0;
  const ratingShares = ratingRows.map(item => ({ ...item, pct: ratingTotal ? (item.count / ratingTotal) * 100 : 0 }));

  const themeRows = (chartData?.radialbar?.data || [])
    .map(item => ({ label: item.label || 'Category', value: Number(item.value || 0) }))
    .filter(item => Number.isFinite(item.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const displayName = user?.firstName || user?.userName || 'there';

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner" />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <AlertCircle className="h-12 w-12" />
        <h3>Failed to load dashboard</h3>
        <p>{error}</p>
        <button onClick={refreshAllData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="dashboard-shell">
      <div className="dashboard-content">
        {/* <section className="dashboard-topbar dashboard-search-only">
          <div className="dashboard-search" role="search">⌕ <span>Search surveys, responses, participants...</span></div>
        </section> */}

        <section className="dashboard-hero">
          <div>
            <h1>Good Morning, {displayName}! 👋</h1>
            <p>{isSuperAdmin ? 'Manage all companies and surveys across the platform' : "Monitor your company's employee engagement and survey performance"}</p>
          </div>
          <div className="dashboard-hero-actions">
            <div className="dashboard-updated">
              {liveMode && <span className="dashboard-live-dot" />}
              <span>Updated {formatLastUpdated(lastUpdated)}</span>
            </div>
            <button className="dashboard-btn dashboard-btn-soft" onClick={() => setLiveMode(v => !v)} title="Automatically refresh dashboard data every 30 seconds">
              <Activity size={16} /> {liveMode ? 'Live' : 'Go Live'}
            </button>
            <button className="dashboard-btn dashboard-btn-primary" onClick={refreshAllData} disabled={refreshing}>
              <RefreshCw size={16} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="dashboard-btn dashboard-btn-create" onClick={handleCreateSurvey}><FileText size={16} /> Create Survey</button>
          </div>
        </section>

        <section className="dashboard-kpis">
          <DashboardMetric icon={<FileText />} title="Active Surveys" value={stats.activeSurveys} trend="Live platform data" tone="blue" />
          <DashboardMetric icon={<Users />} title="Total Responses" value={Number(stats.totalResponses || 0).toLocaleString()} trend="Live response count" tone="purple" />
          <DashboardMetric icon={<Grid />} title="Total Categories" value={Number(stats.totalCategories || 0).toLocaleString()} trend="Across all surveys" tone="green" />
          <DashboardMetric icon={<FileText />} title="Total Questions" value={Number(stats.totalQuestions || 0).toLocaleString()} trend="Across all surveys" tone="gold" />
          <DashboardMetric icon={<Target />} title="Completion Rate" value={`${Number(stats.completionRate || 0).toFixed(1)}%`} trend="Across available invitations" tone="blue" />
          <DashboardMetric icon={<Star />} title="Average Rating" value={ratingAverage ? `${ratingAverage.toFixed(1)} / 5` : '--'} trend={ratingTotal ? `${ratingTotal.toLocaleString()} answers rated` : 'No rating data yet'} tone="gold" />
        </section>

        <section className="dashboard-main-grid">
          <DashboardPanel className="responses-panel" title="Responses Over Time" icon={<BarChart3 />} action={<select value={selectedTimeframe} onChange={e => setSelectedTimeframe(e.target.value)}><option value="7d">Last 7 Days</option><option value="30d">Last 30 Days</option><option value="90d">Last 90 Days</option><option value="1y">Last 1 Year</option></select>}>
            {responseBars.length ? <ThreeDResponseChart data={responseBars} /> : <DashboardEmpty message={chartErrors.dotplot || 'No response data available yet'} />}
          </DashboardPanel>

          <DashboardPanel title="Response Distribution" icon={<PieChart />}>
            {ratingShares.length ? <ThreeDRatingDonut rows={ratingShares} totalResponses={Number(stats.totalResponses || 0)} answerTotal={ratingTotal} /> : <DashboardEmpty message={chartErrors.stackedbar || 'No rating distribution available yet'} />}
          </DashboardPanel>

          <DashboardPanel title="Overall Satisfaction" icon={<Star />} className="satisfaction-panel">
            {ratingAverage ? <ThreeDSatisfactionGauge value={ratingAverage} /> : <DashboardEmpty message="No satisfaction rating data yet" />}
          </DashboardPanel>
        </section>

        <section className="dashboard-structure-grid">
          <DashboardPanel title="Categories by Survey" subtitle="Live category volume across your surveys" icon={<Grid />}>
            {((chartData?.categoryoverview?.data || []).length) ? <ThreeDCategoryChart data={chartData.categoryoverview.data} /> : <DashboardEmpty message={chartErrors.categoryoverview || 'No category data available yet'} />}
          </DashboardPanel>
          <DashboardPanel title="Questions by Survey" subtitle="Live question volume across your surveys" icon={<FileText />}>
            {((chartData?.questionoverview?.data || []).length) ? <ThreeDQuestionChart data={chartData.questionoverview.data} /> : <DashboardEmpty message={chartErrors.questionoverview || 'No question data available yet'} />}
          </DashboardPanel>
        </section>

        <section className="dashboard-bottom-grid">
          <DashboardPanel className="recent-panel" title="Recent Surveys" subtitle="Your latest survey activity and response performance" action={<button className="dashboard-link" onClick={() => navigate('/surveys')}>View All</button>}>
            {recentSurveys.length ? (
              <div className="dashboard-table-wrap">
                <table className="dashboard-table">
                  <thead><tr><th>Title</th><th>Responses</th><th>Completion</th><th>Status</th><th>Created On</th><th></th></tr></thead>
                  <tbody>{recentSurveys.map(survey => {
                    const active = !!survey.isActive;
                    const responseCount = Number(survey.responseCount || 0);
                    return <tr key={survey.id}>
                      <td><strong>{survey.name}</strong><small>{survey.description || 'No description provided'}</small></td>
                      <td className="strong-number">{responseCount.toLocaleString()}</td>
                      <td>{responseCount > 0 ? 'Collected' : '—'}</td>
                      <td><span className={`dashboard-pill ${active ? 'active' : 'inactive'}`}>{active ? 'Active' : 'Inactive'}</span></td>
                      <td>{survey.createdDate ? new Date(survey.createdDate).toLocaleDateString() : '—'}</td>
                      <td><div className="row-actions"><button onClick={() => handleViewSurveyAnalytics(survey.id)}>Analytics</button><button onClick={() => handleViewSurveyResults(survey.id)}>View</button></div></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            ) : <DashboardEmpty message="No surveys yet" />}
          </DashboardPanel>

          <DashboardPanel title="Top Survey Themes" icon={<Award />} action={<button className="dashboard-link" onClick={() => navigate('/surveys')}>View All</button>}>
            {themeRows.length ? <ThreeDThemeBars rows={themeRows} /> : <DashboardEmpty message={chartErrors.radialbar || 'No category data available yet'} />}
            <div className="dashboard-create-card">
              <div className="dashboard-create-icon"><BarChart3 size={24} /></div>
              <div><h3>Create a New Survey</h3><p>Gather valuable feedback in minutes.</p></div>
              <button className="dashboard-btn dashboard-btn-primary" onClick={handleCreateSurvey}>＋ Create Survey</button>
            </div>
          </DashboardPanel>
        </section>
      </div>
    </div>
  );
};

const DashboardMetric = ({ icon, title, value, trend, tone }) => (
  <div className={`dashboard-metric metric-${tone}`}>
    <div className="metric-icon">{icon}</div>
    <div className="metric-title">{title}</div>
    <div className="metric-value">{value}</div>
    <div className="metric-trend">↑ <span>{trend}</span></div>
    <div className="metric-orb" />
  </div>
);

const DashboardPanel = ({ title, subtitle, icon, action, className = '', children }) => (
  <div className={`dashboard-panel ${className}`}>
    <div className="panel-heading">
      <div className="panel-title"><span className="panel-icon">{icon}</span><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></div>
      {action}
    </div>
    {children}
  </div>
);

const DashboardEmpty = ({ message }) => <div className="dashboard-empty"><BarChart3 size={42} /><p>{message}</p></div>;

const ThreeDResponseChart = ({ data }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return <div className="response-chart">
    <div className="response-grid"><i /><i /><i /><i /></div>
    <div className="response-bars">{data.map((item, i) => <div className="response-bar-item" key={`${item.label}-${i}`}>
      <div className="response-bar-value">{item.value.toLocaleString()}</div>
      <div className="response-bar" style={{ height: `${Math.max(12, (item.value / max) * 190)}px` }} />
      <div className="response-label" title={item.label}>{item.label.length > 13 ? `${item.label.slice(0,13)}…` : item.label}</div>
    </div>)}</div>
  </div>;
};

const ThreeDRatingDonut = ({ rows, totalResponses, answerTotal }) => {
  let cursor = 0;
  const colors = ['#158ff0','#25be78','#ffb718','#ff782f','#ed5964'];
  const stops = rows.map((r, i) => { const from = cursor; cursor += r.pct; return `${colors[i % colors.length]} ${from}% ${cursor}%`; }).join(', ');
  return <div className="rating-distribution">
    <div className="donut-3d" style={{ background: `conic-gradient(${stops})` }}><div className="donut-hole"><strong>{Number(totalResponses || 0).toLocaleString()}</strong><span>Total Responses</span><em>{Number(answerTotal || 0).toLocaleString()} answers</em></div></div>
    <div className="rating-legend">{rows.map((r,i)=><div key={r.label}><span className="legend-dot" style={{background:colors[i % colors.length]}} /> <span>{r.label}</span><b>{r.pct.toFixed(1)}%</b></div>)}</div>
  </div>;
};

const ThreeDCategoryChart = ({ data }) => {
  const max = Math.max(...data.map(d => Number(d.value || 0)), 1);
  return <div className="structure-chart category-3d-chart">
    <div className="structure-grid"><i /><i /><i /><i /></div>
    <div className="structure-bars">{data.map((item, i) => {
      const value = Number(item.value || 0);
      return <div className="structure-bar-item" key={`${item.label}-${i}`}>
        <div className="structure-value">{value}</div>
        <div className="structure-bar category-bar" style={{height:`${Math.max(18, value / max * 175)}px`}}><span /></div>
        <div className="structure-label" title={item.label}>{item.label.length > 15 ? `${item.label.slice(0,15)}…` : item.label}</div>
      </div>;
    })}</div>
  </div>;
};

const ThreeDQuestionChart = ({ data }) => {
  const max = Math.max(...data.map(d => Number(d.value || 0)), 1);
  return <div className="structure-chart question-3d-chart">
    <div className="structure-grid"><i /><i /><i /><i /></div>
    <div className="question-columns">{data.map((item, i) => {
      const value = Number(item.value || 0);
      return <div className="question-column-item" key={`${item.label}-${i}`}>
        <div className="structure-value">{value}</div>
        <div className="question-column" style={{height:`${Math.max(22, value / max * 175)}px`}}><span /></div>
        <div className="structure-label" title={item.label}>{item.label.length > 15 ? `${item.label.slice(0,15)}…` : item.label}</div>
      </div>;
    })}</div>
  </div>;
};

const ThreeDSatisfactionGauge = ({ value }) => {
  const pct = Math.max(0, Math.min(100, value / 5 * 100));
  return <div className="satisfaction-gauge-wrap">
    <div className="gauge-3d"><div className="gauge-track" /><div className="gauge-fill" style={{ transform: `rotate(${225 + pct * 2.7}deg)` }} /><div className="gauge-center"><strong>{value.toFixed(1)}</strong><span>out of 5</span></div></div>
    <div className="gauge-quote">“Live rating calculated from collected responses.”</div>
  </div>;
};

const ThreeDThemeBars = ({ rows }) => {
  const max = Math.max(...rows.map(r => r.value), 1);
  return <div className="theme-bars">{rows.map((r,i)=><div className="theme-row" key={`${r.label}-${i}`}><div className="theme-row-top"><span title={r.label}>{r.label}</span><b>{r.value.toFixed(1)}</b></div><div className="theme-track"><div className="theme-fill" style={{width:`${Math.max(3, r.value / max * 100)}%`}} /></div></div>)}</div>;
};

// Enhanced Chart Container Component with Custom Actions
const ChartContainer = ({ 
  title, 
  icon: Icon, 
  iconColor = 'blue', 
  chartType, 
  onRefresh, 
  children, 
  fullWidth = false,
  hasError = false,
  errorMessage = '',
  height = 400,
  customActions = null
}) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
    indigo: 'text-indigo-600',
    gray: 'text-gray-600'
  };

  return (
    <div
      className={`rounded-xl shadow-sm border p-6 ${fullWidth ? 'lg:col-span-2' : ''}`}
      style={{ minHeight: height, background: 'var(--surface)', borderColor: 'var(--tint-line)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Icon className={`h-5 w-5 mr-2 ${colorClasses[iconColor]}`} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>
            {title}
          </h3>
          {hasError && (
            <AlertCircle className="h-4 w-4 ml-2" style={{ color: 'var(--neg)' }} />
          )}
        </div>
        <div className="flex items-center space-x-2">
          {customActions}
          {onRefresh && (
            <button
              onClick={() => onRefresh(chartType)}
              className="text-sm flex items-center space-x-1 transition-colors"
              style={{ color: 'var(--accent)' }}
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          )}
        </div>
      </div>
      {hasError && errorMessage && (
        <div className="mb-4 p-3 rounded-lg border" style={{ background: 'var(--b-red-bg)', borderColor: 'var(--tint-line)' }}>
          <p className="text-sm" style={{ color: 'var(--b-red-fg)' }}>{errorMessage}</p>
        </div>
      )}
      <div className="h-full">
        {children}
      </div>
    </div>
  );
};

// Chart Fallback Component
const ChartFallback = ({ message, chartType, height = 300 }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'bullet': return Target;
      case 'dotplot': return BarChart2;
      case 'radialbar': return PieChart;
      case 'nightingale': return Grid;
      case 'stackedbar': return BarChart3;
      case 'stackedcolumn': return Calendar;
      case 'plot': return Activity;
      case 'table': return Table;
      case 'employeeReport': return Users2;
      default: return BarChart;
    }
  };

  const Icon = getIcon(chartType);

  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{ height: height - 100, color: 'var(--muted)' }}
    >
      <Icon className="h-16 w-16 mb-4" style={{ color: 'var(--faint)' }} />
      <p className="text-center" style={{ color: 'var(--muted)' }}>{message}</p>
      <p className="text-sm mt-2 text-center" style={{ color: 'var(--faint)' }}>
        This chart will appear when data becomes available
      </p>
    </div>
  );
};

export default Dashboard;